/*
 * Normaliserad importmodell för GymTracker.
 *
 * Alla källor (Daily Strength-CSV, Google Takeout-zip, …) tolkas till samma
 * "bundle" så att GUI:t, dedup-logiken och import-körningen är källoberoende:
 *
 *   bundle = {
 *     source: 'daily_strength' | 'google_takeout',
 *     files: [{ name, size }],
 *     mock: bool,
 *     datasets: { sessions, activities, weights, measurements, height, steps, sleep, heartRate }
 *   }
 *
 * Varje dataset: { key, items: [...], supported, enabled, extra... }.
 * Varje item har `status` ('new' | 'duplicate' | 'overlap' | 'update' | 'invalid'),
 * `statusReason` och `selected`. Dedup/överlapp sätts av applyDedup() mot data
 * som redan finns på servern (eller mock-data i steg 1).
 *
 * Skalning: en Takeout kan innehålla flera års data (tusentals poster). Därför
 * är `items`-arrayerna markRaw (ingen djup reaktivitet) och GUI:t läser
 * `importState.rev` som bumpas via touch() när poster ändras. Listor renderas
 * grupperade per månad och fönstrade (se useTimeline.js).
 */
import { markRaw } from 'vue';

export const SOURCES = {
  daily_strength: {
    key: 'daily_strength',
    label: 'Daily Strength',
    short: 'Daily Strength',
    icon: '🏋️',
    hint: 'CSV-export från gymappen (pass, vikt, kroppsmått)',
    accepts: ['.csv', '.txt', '.zip'],
  },
  google_takeout: {
    key: 'google_takeout',
    label: 'Google Takeout',
    short: 'Google',
    icon: 'G',
    hint: 'Fit/Hälsa-zip från takeout.google.com (aktiviteter, vikt, längd, steg)',
    accepts: ['.zip'],
  },
};

// Ordning, ikon och text för datatyp-korten. `unit` används i sammanfattningar.
export const DATASET_DEFS = [
  { key: 'sessions',     label: 'Pass',        icon: '🏋️', unit: 'pass',        desc: 'Gympass med övningar och set' },
  { key: 'activities',   label: 'Aktiviteter', icon: '❤️', unit: 'aktiviteter', desc: 'Promenader, löpning, cykling …' },
  { key: 'weights',      label: 'Vikt',        icon: '⚖️', unit: 'mätningar',   desc: 'Kroppsvikt och kroppssammansättning' },
  { key: 'measurements', label: 'Kroppsmått',  icon: '📏', unit: 'mättillfällen', desc: 'Midja, bröst, armar …' },
  { key: 'height',       label: 'Längd',       icon: '📐', unit: 'värden',      desc: 'Senaste värdet sparas på profilen' },
  { key: 'steps',        label: 'Steg',        icon: '👣', unit: 'dagar',       desc: 'Dagliga steg' },
  { key: 'sleep',        label: 'Sömn',        icon: '😴', unit: 'nätter',      desc: 'Stöds inte än', unsupported: true },
  { key: 'heartRate',    label: 'Puls',        icon: '💓', unit: 'mätningar',   desc: 'Stöds inte än', unsupported: true },
];

export const DATASET_BY_KEY = Object.fromEntries(DATASET_DEFS.map(d => [d.key, d]));

// Kroppsmått som kolumner på BodyMeasurement (beslut 2026-09-05).
export const MEASUREMENT_KINDS = [
  { key: 'neckCm',       label: 'Hals' },
  { key: 'shouldersCm',  label: 'Skuldror' },
  { key: 'chestCm',      label: 'Bröst' },
  { key: 'waistCm',      label: 'Midja' },
  { key: 'hipsCm',       label: 'Höft' },
  { key: 'bicepsLeftCm', label: 'Biceps V' },
  { key: 'bicepsRightCm',label: 'Biceps H' },
  { key: 'forearmLeftCm', label: 'Underarm V' },
  { key: 'forearmRightCm',label: 'Underarm H' },
  { key: 'thighLeftCm',  label: 'Lår V' },
  { key: 'thighRightCm', label: 'Lår H' },
  { key: 'calfLeftCm',   label: 'Vad V' },
  { key: 'calfRightCm',  label: 'Vad H' },
];

export const STATUS_LABELS = {
  new:       { label: 'Ny',              tone: 'ok' },
  update:    { label: 'Uppdaterar',      tone: 'info' },
  duplicate: { label: 'Finns redan',     tone: 'grey' },
  overlap:   { label: 'Överlappar pass', tone: 'warn' },
  invalid:   { label: 'Ogiltig',         tone: 'danger' },
};

let localId = 0;
export function nextLocalId(prefix = 'i') { return `${prefix}${++localId}`; }

export function emptyDataset(key, extra = {}) {
  const def = DATASET_BY_KEY[key];
  return {
    key,
    items: markRaw([]),
    supported: !def?.unsupported,
    enabled: !def?.unsupported,
    // Antal poster som fanns i källan men inte kan importeras (t.ex. sömn).
    skippedCount: 0,
    ...extra,
  };
}

export function createBundle(source, files = [], { mock = false } = {}) {
  const datasets = {};
  for (const d of DATASET_DEFS) datasets[d.key] = emptyDataset(d.key);
  datasets.sessions.mappings = [];       // övningsmappningar (csvName → övning)
  datasets.activities.typeMappings = []; // Google-typ → aktivitetstyp
  return {
    source,
    sourceLabel: SOURCES[source]?.label ?? source,
    files: files.map(f => ({ name: f.name, size: f.size })),
    parsedAt: new Date().toISOString(),
    mock,
    datasets,
  };
}

// ── Hjälpare ─────────────────────────────────────────────────────────────

export function dayKey(iso) {
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '';
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

// Enkel, stabil hash (djb2) för externalId när källan saknar egna id:n.
export function stableHash(str) {
  let h = 5381;
  for (let i = 0; i < str.length; i++) h = ((h << 5) + h + str.charCodeAt(i)) | 0;
  return (h >>> 0).toString(36);
}

export function overlapSeconds(aStart, aEnd, bStart, bEnd) {
  const s = Math.max(aStart, bStart), e = Math.min(aEnd, bEnd);
  return Math.max(0, (e - s) / 1000);
}

// ── Fuzzy-matchning av övningsnamn ───────────────────────────────────────

export function normalizeName(s) {
  return String(s || '')
    .toLowerCase()
    .replace(/[()[\]{}.,;:!?'"´`]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

function levenshteinSimilarity(a, b) {
  if (a === b) return 1;
  if (!a.length || !b.length) return 0;
  let prev = Array.from({ length: b.length + 1 }, (_, i) => i);
  for (let i = 1; i <= a.length; i++) {
    const curr = [i];
    for (let j = 1; j <= b.length; j++) {
      curr[j] = a[i - 1] === b[j - 1] ? prev[j - 1] : 1 + Math.min(prev[j - 1], prev[j], curr[j - 1]);
    }
    prev = curr;
  }
  return 1 - prev[b.length] / Math.max(a.length, b.length);
}

function tokenSimilarity(a, b) {
  const ta = new Set(a.split(' ')), tb = new Set(b.split(' '));
  if (!ta.size || !tb.size) return 0;
  let common = 0;
  for (const t of ta) if (tb.has(t)) common++;
  return common / Math.max(ta.size, tb.size);
}

// 0–1. Kombinerar teckenlikhet och ordöverlapp så "Barbell Bench Press" ≈ "Bench Press (Barbell)".
export function nameSimilarity(a, b) {
  const na = normalizeName(a), nb = normalizeName(b);
  if (!na || !nb) return 0;
  if (na === nb) return 1;
  let score = Math.max(levenshteinSimilarity(na, nb), tokenSimilarity(na, nb) * 0.95);
  // Innehållstest utan blanksteg: "lat pulldown" ⊂ "wide grip lat pull down".
  const ca = na.replace(/\s/g, ''), cb = nb.replace(/\s/g, '');
  if (ca.length >= 6 && cb.length >= 6 && (ca.includes(cb) || cb.includes(ca))) {
    score = Math.max(score, 0.62 + 0.3 * Math.min(ca.length, cb.length) / Math.max(ca.length, cb.length));
  }
  return score;
}

// Under 0,72 blir för många falska träffar ("Leg Press" ≈ "Bench Press" 64 %).
export const MATCH_THRESHOLD = 0.72;

export function bestExerciseMatch(name, exerciseTypes) {
  let best = null, bestScore = 0;
  for (const et of exerciseTypes || []) {
    const score = nameSimilarity(name, et.name);
    if (score > bestScore) { bestScore = score; best = et; }
  }
  return { exerciseType: best, score: bestScore };
}

// Gissa muskelgrupp för en ny övning utifrån nyckelord i namnet. Returnerar
// en lista av sökord som jämförs mot de faktiska muskelgruppsnamnen.
const MUSCLE_KEYWORDS = [
  { re: /bench|press(?!.*(shoulder|military|arnold|cuban))|fly|butterfly|pec|bröst|dips/i, groups: ['bröst', 'chest'] },
  { re: /squat|leg|calf|lunge|ben|vad|knäböj|hack/i, groups: ['ben', 'legs', 'lår'] },
  { re: /curl(?!.*leg)|bicep|tricep|pushdown|skull|wrist|arm|forearm|underarm/i, groups: ['arm', 'biceps', 'triceps'] },
  { re: /row|pull|lat|deadlift|marklyft|rygg|back|hyperextension|chin|drag/i, groups: ['rygg', 'back'] },
  { re: /situp|sit-up|crunch|side bend|plank|ab|mage|core|bål/i, groups: ['mage', 'core', 'abs', 'bål'] },
  { re: /shoulder|military|raise|arnold|cuban|delt|axel|skuldr|overhead|ohp/i, groups: ['axl', 'skuldr', 'shoulder', 'delt'] },
  { re: /farmer|carry|walk|log|yoke|joke|sköld|shield|bär/i, groups: ['hel', 'full', 'strongman', 'kropp'] },
];

export function guessMuscleGroup(name, muscleGroups) {
  if (!muscleGroups?.length) return null;
  for (const rule of MUSCLE_KEYWORDS) {
    if (!rule.re.test(name)) continue;
    const hit = muscleGroups.find(g => rule.groups.some(k => normalizeName(g.name).includes(k)));
    if (hit) return hit.id;
  }
  return null;
}

// Gissa mätflaggor för en ny övning från de set som finns i importen.
export function guessExerciseFlags(sets) {
  const hasWeight = sets.some(s => s.weight != null && s.weight > 0);
  const hasReps = sets.some(s => s.reps != null && s.reps > 0);
  const hasDistance = sets.some(s => s.distanceMeters != null && s.distanceMeters > 0);
  const hasDuration = sets.some(s => s.durationSeconds != null && s.durationSeconds > 0);
  return {
    tracksWeight: hasWeight,
    tracksReps: hasReps,
    tracksDistance: hasDistance,
    tracksDuration: hasDuration,
    isBodyweight: !hasWeight && hasReps,
  };
}

// ── Övningsmappningar ────────────────────────────────────────────────────

/*
 * Bygger mappningslistan för pass-datasetet. `aliases` är sparade mappningar
 * (csvName → exerciseTypeId) från tidigare importer; de vinner över fuzzy.
 */
export function buildExerciseMappings(sessions, { exerciseTypes = [], muscleGroups = [], aliases = {} } = {}) {
  const groups = new Map();
  for (const s of sessions) {
    for (const ex of s.exercises) {
      const g = groups.get(ex.csvName) || { csvName: ex.csvName, setCount: 0, sessionCount: 0, sets: [] };
      g.setCount += ex.sets.length;
      g.sessionCount += 1;
      g.sets.push(...ex.sets);
      groups.set(ex.csvName, g);
    }
  }
  const byId = new Map(exerciseTypes.map(et => [et.id, et]));
  // Alias matchas normaliserat (servern lagrar trim + lowercase).
  const aliasLookup = new Map(Object.entries(aliases).map(([k, v]) => [normalizeName(k), v]));
  return [...groups.values()]
    .sort((a, b) => b.setCount - a.setCount)
    .map(g => {
      const aliasId = aliasLookup.get(normalizeName(g.csvName));
      const { exerciseType, score } = bestExerciseMatch(g.csvName, exerciseTypes);
      const flags = guessExerciseFlags(g.sets);
      let action = 'create', exerciseTypeId = null, fromAlias = false;
      if (aliasId && byId.has(aliasId)) { action = 'map'; exerciseTypeId = aliasId; fromAlias = true; }
      else if (exerciseType && score >= MATCH_THRESHOLD) { action = 'map'; exerciseTypeId = exerciseType.id; }
      return {
        csvName: g.csvName,
        setCount: g.setCount,
        sessionCount: g.sessionCount,
        action,
        exerciseTypeId,
        fromAlias,
        autoScore: Math.round(score * 100),
        suggestedId: exerciseType?.id ?? null,
        create: {
          name: g.csvName,
          muscleGroupId: guessMuscleGroup(g.csvName, muscleGroups),
          muscleIds: [],
          ...flags,
        },
      };
    });
}

export function mappingIsReady(m) {
  return m.action === 'ignore' || m.action === 'create' || (m.action === 'map' && !!m.exerciseTypeId);
}

// ── Dedup / överlapp ─────────────────────────────────────────────────────

/*
 * existing = {
 *   sessions:     [{ externalId?, name, startTime, endTime, date }],
 *   activities:   [{ externalId?, startedAt, durationSeconds }],
 *   weights:      [{ date, source? }],
 *   measurements: [{ date }],
 *   steps:        [{ date, steps }],
 * }
 * Muterar items (status/selected). Pass-överlapp tittar på både serverns pass
 * och pass i samma bundle. Google-aktiviteter av gym-typ som överlappar ett
 * pass blir 'overlap' och avbockade (beslut 2026-09-05).
 */
export function applyDedup(bundle, existing = {}) {
  const ds = bundle.datasets;
  const exSessions = existing.sessions || [];
  const exActivities = existing.activities || [];
  const exWeights = existing.weights || [];
  const exMeasurements = existing.measurements || [];
  const exSteps = existing.steps || [];

  // Pass
  const exSessionIds = new Set(exSessions.map(s => s.externalId).filter(Boolean));
  const exSessionKeys = new Set(exSessions.map(s => `${dayKey(s.startTime || s.date)}|${normalizeName(s.name)}`));
  for (const s of ds.sessions.items) {
    if (exSessionIds.has(s.externalId)) setStatus(s, 'duplicate', 'Samma pass är redan importerat');
    else if (exSessionKeys.has(`${dayKey(s.startedAt)}|${normalizeName(s.name)}`)) setStatus(s, 'duplicate', 'Pass med samma namn finns redan den dagen');
    else setStatus(s, 'new');
  }

  // Aktiviteter. Passfönstren sorteras på start så varje aktivitet bara jämförs
  // med fönster som kan överlappa (binärsökning + kort skanning) – O((n+m) log m)
  // i stället för n×m, vilket spelar roll vid flera års data.
  const exActIds = new Set(exActivities.map(a => a.externalId).filter(Boolean));
  const sessionWindows = [
    ...exSessions.map(s => windowOf(s.startTime || s.date, s.endTime, 3600, s.name)),
    ...ds.sessions.items.map(s => windowOf(s.startedAt, s.endedAt, s.durationSeconds || 3600, s.name)),
  ].filter(Boolean).sort((x, y) => x.start - y.start);
  const MAX_WINDOW_MS = 6 * 3600e3;
  for (const a of ds.activities.items) {
    if (a.invalid) { setStatus(a, 'invalid', a.invalid); continue; }
    if (exActIds.has(a.externalId)) { setStatus(a, 'duplicate', 'Aktiviteten är redan importerad'); continue; }
    const aStart = new Date(a.startedAt).getTime();
    const aEnd = aStart + (a.durationSeconds || 0) * 1000;
    const minOverlap = Math.min(600, (a.durationSeconds || 0) * 0.5);
    let hit = null;
    for (let i = lowerBound(sessionWindows, aStart - MAX_WINDOW_MS); i < sessionWindows.length && sessionWindows[i].start <= aEnd; i++) {
      const w = sessionWindows[i];
      if (overlapSeconds(aStart, aEnd, w.start, w.end) >= minOverlap) { hit = w; break; }
    }
    if (hit) setStatus(a, 'overlap', `Överlappar passet "${hit.name}"`);
    else setStatus(a, 'new');
  }

  // Vikt: en per dag. Samma dag på servern ⇒ dubblett. Inom bundlen: första vinner.
  const exWeightDays = new Set(exWeights.map(w => dayKey(w.date)));
  const seenWeightDays = new Set();
  for (const w of ds.weights.items) {
    const day = dayKey(w.date);
    if (exWeightDays.has(day)) setStatus(w, 'duplicate', 'Vikt finns redan den dagen');
    else if (seenWeightDays.has(day)) setStatus(w, 'duplicate', 'Flera vikter samma dag – första behålls');
    else { setStatus(w, 'new'); seenWeightDays.add(day); }
  }

  // Kroppsmått: servern lägger dem på dagens viktrad. Finns rad ⇒ "uppdaterar",
  // kommer vikten i samma import ⇒ ny, annars går de inte att spara.
  const exMeasDays = new Set(exMeasurements.map(m => dayKey(m.date)));
  const bundleWeightDays = new Set(ds.weights.items.map(w => dayKey(w.date)));
  for (const m of ds.measurements.items) {
    const day = dayKey(m.date);
    if (m.weightKg > 0 || bundleWeightDays.has(day)) setStatus(m, 'new');
    else if (exMeasDays.has(day)) setStatus(m, 'update', 'Fyller på dagens viktrad');
    else setStatus(m, 'invalid', 'Kroppsmått kräver en viktmätning samma dag');
  }

  // Steg: högre värde uppdaterar, lika/lägre ⇒ dubblett.
  const exStepsByDay = new Map(exSteps.map(s => [dayKey(s.date), s.steps]));
  for (const s of ds.steps.items) {
    const cur = exStepsByDay.get(dayKey(s.date));
    if (cur == null) setStatus(s, 'new');
    else if (s.steps > cur) setStatus(s, 'update', `Höjer från ${cur} steg`);
    else setStatus(s, 'duplicate', 'Dagen finns redan');
  }

  // Längd: bara det senaste värdet är intressant.
  ds.height.items.sort((a, b) => new Date(b.date) - new Date(a.date));
  ds.height.items.forEach((h, i) => setStatus(h, i === 0 ? 'new' : 'duplicate', i === 0 ? 'Sparas på profilen' : 'Äldre värde'));

  return bundle;
}

// Första index i (start-sorterad) lista vars start >= t.
function lowerBound(windows, t) {
  let lo = 0, hi = windows.length;
  while (lo < hi) { const mid = (lo + hi) >> 1; if (windows[mid].start < t) lo = mid + 1; else hi = mid; }
  return lo;
}

function windowOf(start, end, fallbackSeconds, name = '') {
  const s = new Date(start).getTime();
  if (isNaN(s)) return null;
  const e = end ? new Date(end).getTime() : s + fallbackSeconds * 1000;
  return { start: s, end: e, name };
}

function setStatus(item, status, reason = '') {
  item.status = status;
  item.statusReason = reason;
  // Förval: nya och uppdateringar bockade, allt annat avbockat.
  item.selected = status === 'new' || status === 'update';
}

// ── Sammanfattning per dataset ───────────────────────────────────────────

export function datasetCounts(ds) {
  const c = { total: ds.items.length, selected: 0, new: 0, update: 0, duplicate: 0, overlap: 0, invalid: 0 };
  for (const it of ds.items) {
    c[it.status || 'new'] = (c[it.status || 'new'] || 0) + 1;
    if (it.selected) c.selected++;
  }
  return c;
}

// Datumintervall för ett dataset: { from, to, years } eller null.
export function datasetDateRange(ds) {
  let min = Infinity, max = -Infinity;
  for (const it of ds.items) {
    const t = new Date(it.startedAt || it.date).getTime();
    if (isNaN(t)) continue;
    if (t < min) min = t;
    if (t > max) max = t;
  }
  if (min === Infinity) return null;
  return { from: new Date(min), to: new Date(max), years: (max - min) / (365.25 * 86400e3) };
}

export function formatRange(r) {
  if (!r) return '';
  const f = d => d.toLocaleDateString('sv-SE', { year: 'numeric', month: 'short' });
  const span = r.years >= 1 ? ` · ${r.years.toFixed(1).replace('.', ',')} år` : '';
  return `${f(r.from)} → ${f(r.to)}${span}`;
}

export const fmtInt = n => Number(n || 0).toLocaleString('sv-SE');

// Antal poster som faktiskt importeras från ett dataset (0 om avstängt).
export function datasetPlannedCount(ds) {
  if (!ds.supported || !ds.enabled) return 0;
  return ds.items.filter(i => i.selected).length;
}

export function bundlePlannedTotal(bundle) {
  return Object.values(bundle.datasets).reduce((n, ds) => n + datasetPlannedCount(ds), 0);
}

// Pass-datasetet är redo när alla övningsmappningar är lösta.
export function sessionsBlockingReason(bundle) {
  const ds = bundle.datasets.sessions;
  if (!ds.enabled || !ds.items.some(i => i.selected)) return null;
  const unresolved = ds.mappings.filter(m => !mappingIsReady(m)).length;
  return unresolved > 0 ? `${unresolved} övning${unresolved > 1 ? 'ar' : ''} saknar koppling` : null;
}

export function activitiesBlockingReason(bundle) {
  const ds = bundle.datasets.activities;
  if (!ds.enabled || !ds.items.some(i => i.selected)) return null;
  const used = new Set(ds.items.filter(i => i.selected).map(i => i.googleType));
  const unresolved = ds.typeMappings.filter(t => used.has(t.googleType) && t.action === 'map' && !t.activityTypeId).length;
  return unresolved > 0 ? `${unresolved} aktivitetstyp${unresolved > 1 ? 'er' : ''} saknar koppling` : null;
}
